import { Trans, useTranslation } from "react-i18next";
import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import type { TimelineAnnotation } from "./AnnotationTimeline";
import type { ReviewedTimeRange } from "./eegReviewProgressUtils";
import {
  buildNavigationMajorTickTimes,
  chooseNavigationTickIntervalSeconds,
  clampNavigationTimelineVisibleRange,
  formatNavigationTimelineTickLabel,
  isDurationAnnotation,
  isNavigationTimelineSlidingWindow,
  MACRO_OVERVIEW_ZOOM_MIN_SPAN_SECONDS,
  NAV_LABEL_MIN_SPACING_PX,
  NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS,
  NAV_TIMELINE_TICK_BAND_PX,
  shouldDrawNavigationTickLabel,
  type MacroOverviewZoomMode,
  type NavigationTimelineVisibleRange,
  resolveMacroOverviewVisibleRange,
} from "./eegNavigationTimelineUtils";
import {
  hitTestTimelinePageMarker,
  resolvePageMarkerPanSeek,
  resolveTimelineOverviewPointerSeek,
} from "./eegNavigationTimelineInteraction";
import { resolveEegOverviewPalette } from "../utils/eegWorkspaceTheme";
import "./WaveformMinimap.css";

export interface WaveformMinimapProps {
  totalDuration: number;
  recordingStartMs?: number | null;
  viewportStart: number;
  viewportEnd: number;
  playheadTime?: number;
  playheadTimeRef?: React.MutableRefObject<number>;
  /** Trackit reading-line offset added to viewport/playheadRef seconds. */
  playheadPageOffsetSeconds?: number;
  isPlaying?: boolean;
  annotations: TimelineAnnotation[];
  zoomMode: MacroOverviewZoomMode;
  customRange?: NavigationTimelineVisibleRange | null;
  onZoomModeChange: (mode: MacroOverviewZoomMode) => void;
  onCustomRangeChange: (range: NavigationTimelineVisibleRange) => void;
  onSeek: (newStart: number) => void;
  reviewedRanges?: ReviewedTimeRange[];
  videoSyncActive?: boolean;
  /** When false, header is rendered by the parent stack (Phase 4 merged chrome). */
  showHeader?: boolean;
  /** Wall-clock / elapsed tick labels in the top band (height budget is fixed). */
  showTimeTicks?: boolean;
  /** Slim overview strip for collapsed macro mode (still interactive). */
  compact?: boolean;
  /** Workspace paper theme — canvas fills ignore CSS chrome tokens. */
  workspaceTheme?: string | null;
  /**
   * CSS px inset matching the waveform plot left (dedicated channel label column).
   * Overview track / canvas align to the waveform start; title sits in the gutter.
   */
  plotLeftPx?: number;
}

const MINIMAP_BODY_HEIGHT = 36 - NAV_TIMELINE_TICK_BAND_PX;
const MINIMAP_HEIGHT = MINIMAP_BODY_HEIGHT + NAV_TIMELINE_TICK_BAND_PX;
/** Tall enough for overview context + in-strip expand control (not taller than the button). */
const COMPACT_MINIMAP_HEIGHT = 20;
const ZOOM_BAR_HEIGHT = 14;

function bucketWidthForMacro(rangeSeconds: number, canvasWidth: number): number {
  if (rangeSeconds <= NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS) {
    return 3;
  }
  const secondsPerPixel = rangeSeconds / Math.max(1, canvasWidth);
  if (secondsPerPixel > 120) {
    return 4;
  }
  return 3;
}

type ZoomBarDragMode = "pan" | "resize-start" | "resize-end";

const WaveformMinimap: React.FC<WaveformMinimapProps> = ({
  totalDuration,
  recordingStartMs = null,
  viewportStart,
  viewportEnd,
  playheadTime,
  playheadTimeRef,
  playheadPageOffsetSeconds = 0,
  isPlaying = false,
  annotations,
  zoomMode,
  customRange = null,
  onZoomModeChange,
  onCustomRangeChange,
  onSeek,
  reviewedRanges = [],
  videoSyncActive = false,
  showHeader = true,
  showTimeTicks = true,
  compact = false,
  workspaceTheme = "dark",
  plotLeftPx = 0,
}) => {
  const { t } = useTranslation("features");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomBarRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [hoveredAnnotation, setHoveredAnnotation] =
    useState<TimelineAnnotation | null>(null);
  const [tooltipX, setTooltipX] = useState(0);
  const [livePlayheadTime, setLivePlayheadTime] = useState<number | null>(null);
  const isScrubbingRef = useRef(false);
  const pageDragRef = useRef<{
    startPointerX: number;
    initialPageStart: number;
  } | null>(null);
  const zoomDragRef = useRef<{
    mode: ZoomBarDragMode;
    startClientX: number;
    initialRange: NavigationTimelineVisibleRange;
  } | null>(null);

  const safeDuration = Math.max(1, totalDuration);
  const resolvedPlayheadTime = Math.max(
    0,
    Math.min(
      safeDuration,
      livePlayheadTime ??
        playheadTime ??
        viewportStart + Math.max(0, playheadPageOffsetSeconds),
    ),
  );
  const showMacroControls =
    !compact && isNavigationTimelineSlidingWindow(safeDuration);
  // Label + blue range thumb only when overview is windowed (1h / 30m / custom).
  // Full-study mode hides this chrome; zoom still changes from the playback select.
  const showRangeChrome =
    showHeader && showMacroControls && zoomMode !== "full";
  const minimapHeight = compact ? COMPACT_MINIMAP_HEIGHT : MINIMAP_HEIGHT;
  const visibleRange = useMemo(
    () =>
      resolveMacroOverviewVisibleRange({
        totalDuration: safeDuration,
        playheadTime: resolvedPlayheadTime,
        zoomMode,
        customRange,
      }),
    [safeDuration, resolvedPlayheadTime, zoomMode, customRange],
  );
  const rangeDuration = Math.max(
    0.1,
    visibleRange.rangeEnd - visibleRange.rangeStart,
  );
  const pageDuration = Math.max(0.1, viewportEnd - viewportStart);
  const pixelsPerSecond = canvasWidth / rangeDuration;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) {
        const inset = Math.max(0, Math.round(plotLeftPx || 0));
        setCanvasWidth(Math.max(1, Math.floor(w - inset)));
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [plotLeftPx]);

  useEffect(() => {
    if (!isPlaying || !playheadTimeRef) {
      setLivePlayheadTime(null);
      return;
    }

    let rafId = 0;
    const tick = () => {
      setLivePlayheadTime(
        playheadTimeRef.current + Math.max(0, playheadPageOffsetSeconds),
      );
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [isPlaying, playheadPageOffsetSeconds, playheadTimeRef]);

  const timeToX = useCallback(
    (time: number) =>
      ((time - visibleRange.rangeStart) / rangeDuration) * canvasWidth,
    [canvasWidth, rangeDuration, visibleRange.rangeStart],
  );

  const xToTime = useCallback(
    (x: number) =>
      visibleRange.rangeStart +
      (x / Math.max(1, canvasWidth)) * rangeDuration,
    [canvasWidth, rangeDuration, visibleRange.rangeStart],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = minimapHeight * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${minimapHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const tickBandHeight =
      !compact && showTimeTicks ? NAV_TIMELINE_TICK_BAND_PX : 0;
    const bodyTop = tickBandHeight;
    const bodyHeight = Math.max(compact ? 10 : 10, minimapHeight - bodyTop);
    const overviewPalette = resolveEegOverviewPalette(workspaceTheme);

    ctx.fillStyle = overviewPalette.bg;
    ctx.fillRect(0, 0, canvasWidth, minimapHeight);

    if (showTimeTicks) {
      const tickInterval = chooseNavigationTickIntervalSeconds(
        rangeDuration,
        canvasWidth,
        NAV_LABEL_MIN_SPACING_PX,
      );
      const majorTickTimes = buildNavigationMajorTickTimes(
        rangeDuration,
        tickInterval,
      ).map((time) => time + visibleRange.rangeStart);

      ctx.fillStyle = overviewPalette.tickBand;
      ctx.fillRect(0, 0, canvasWidth, tickBandHeight);
      ctx.strokeStyle = overviewPalette.tick;
      ctx.beginPath();
      ctx.moveTo(0, tickBandHeight - 0.5);
      ctx.lineTo(canvasWidth, tickBandHeight - 0.5);
      ctx.stroke();

      ctx.fillStyle = overviewPalette.label;
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      let previousLabelRightEdge = -Infinity;
      for (const time of majorTickTimes) {
        const x = timeToX(time);
        if (x < 0 || x > canvasWidth) continue;

        const label = formatNavigationTimelineTickLabel(time, recordingStartMs);
        const labelWidth = ctx.measureText(label).width;
        const showLabel = shouldDrawNavigationTickLabel(
          x,
          labelWidth,
          previousLabelRightEdge,
        );

        ctx.strokeStyle = showLabel
          ? overviewPalette.tickMajor
          : overviewPalette.tick;
        ctx.beginPath();
        ctx.moveTo(x, showLabel ? tickBandHeight - 4 : tickBandHeight - 2);
        ctx.lineTo(x, tickBandHeight);
        ctx.stroke();

        if (showLabel) {
          ctx.fillText(label, x, tickBandHeight / 2);
          previousLabelRightEdge = x + labelWidth / 2;
        }
      }
    }

    reviewedRanges.forEach((range) => {
      if (range.end < visibleRange.rangeStart || range.start > visibleRange.rangeEnd) {
        return;
      }
      const startX = timeToX(
        Math.max(visibleRange.rangeStart, range.start),
      );
      const endX = timeToX(Math.min(visibleRange.rangeEnd, range.end));
      const width = Math.max(1, endX - startX);
      ctx.fillStyle = overviewPalette.reviewedFill;
      ctx.fillRect(startX, bodyTop, width, bodyHeight);
    });

    const minuteInterval = 60;
    ctx.strokeStyle = overviewPalette.grid;
    ctx.lineWidth = 1;
    for (
      let time = Math.ceil(visibleRange.rangeStart / minuteInterval) * minuteInterval;
      time <= visibleRange.rangeEnd;
      time += minuteInterval
    ) {
      const x = timeToX(time);
      ctx.beginPath();
      ctx.moveTo(x, bodyTop);
      ctx.lineTo(x, minimapHeight);
      ctx.stroke();
    }

    annotations.forEach((ann) => {
      if (
        ann.startTime < visibleRange.rangeStart ||
        ann.startTime > visibleRange.rangeEnd
      ) {
        return;
      }
      if (isDurationAnnotation(ann)) {
        const startX = timeToX(
          Math.max(visibleRange.rangeStart, ann.startTime),
        );
        const endX = timeToX(Math.min(visibleRange.rangeEnd, ann.endTime));
        const width = Math.max(2, endX - startX);
        const isHovered = hoveredAnnotation?.id === ann.id;
        ctx.fillStyle = `${ann.color}${isHovered ? "ee" : "bb"}`;
        ctx.fillRect(startX, bodyTop + 6, width, 10);
        ctx.strokeStyle = isHovered ? "#f8fafc" : ann.color;
        ctx.lineWidth = isHovered ? 1.25 : 1;
        ctx.strokeRect(startX + 0.5, bodyTop + 6.5, width - 1, 9);
      }
    });

    const macroRangeSeconds =
      visibleRange.rangeEnd - visibleRange.rangeStart;
    const useDensityBuckets =
      macroRangeSeconds > NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS;
    const instantAnnotations = annotations.filter(
      (ann) =>
        !isDurationAnnotation(ann) &&
        ann.startTime >= visibleRange.rangeStart &&
        ann.startTime <= visibleRange.rangeEnd,
    );

    if (useDensityBuckets && instantAnnotations.length > 0) {
      const bucketWidth = 3;
      const bucketCount = Math.max(1, Math.ceil(canvasWidth / bucketWidth));
      const bucketCounts = new Uint16Array(bucketCount);
      instantAnnotations.forEach((ann) => {
        const bucket = Math.min(
          bucketCount - 1,
          Math.max(0, Math.floor(timeToX(ann.startTime) / bucketWidth)),
        );
        bucketCounts[bucket] = Math.min(65535, bucketCounts[bucket] + 1);
      });
      const maxCount = bucketCounts.reduce(
        (max, count) => Math.max(max, count),
        1,
      );
      const laneTop = minimapHeight - (compact ? 7 : 11);
      const laneHeight = compact ? 5 : 9;
      for (let bucket = 0; bucket < bucketCount; bucket += 1) {
        const count = bucketCounts[bucket];
        if (count <= 0) continue;
        const x = bucket * bucketWidth;
        const barHeight = Math.max(
          2,
          Math.round((count / maxCount) * laneHeight),
        );
        ctx.fillStyle = "rgba(251, 191, 36, 0.82)";
        ctx.fillRect(
          x,
          laneTop + (laneHeight - barHeight),
          bucketWidth - 1,
          barHeight,
        );
      }
    } else {
      instantAnnotations.forEach((ann) => {
        const x = timeToX(ann.startTime);
        const isHovered = hoveredAnnotation?.id === ann.id;
        const markWidth = Math.max(3, bucketWidthForMacro(macroRangeSeconds, canvasWidth));
        ctx.fillStyle = `${ann.color}${isHovered ? "ff" : "dd"}`;
        ctx.fillRect(x - markWidth / 2, bodyTop + 5, markWidth, bodyHeight - 10);
        if (isHovered) {
          ctx.strokeStyle = "#f8fafc";
          ctx.lineWidth = 1;
          ctx.strokeRect(
            x - markWidth / 2 + 0.5,
            bodyTop + 5.5,
            markWidth - 1,
            bodyHeight - 11,
          );
        }
      });
    }

    const vpX1 = timeToX(viewportStart);
    const vpX2 = timeToX(viewportEnd);
    const vpW = Math.max(2, vpX2 - vpX1);

    ctx.fillStyle = "rgba(239, 68, 68, 0.24)";
    ctx.fillRect(vpX1, bodyTop, vpW, bodyHeight);
    ctx.strokeStyle = "rgba(239, 68, 68, 0.95)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(vpX1 + 0.5, bodyTop + 0.5, vpW - 1, bodyHeight - 1);

    const playheadX = timeToX(resolvedPlayheadTime);
    if (Math.abs(playheadX - vpX1) > 1.5) {
      ctx.strokeStyle = "rgba(248, 113, 113, 0.9)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(playheadX + 0.5, bodyTop);
      ctx.lineTo(playheadX + 0.5, minimapHeight);
      ctx.stroke();
    }

    if (videoSyncActive) {
      ctx.strokeStyle = "rgba(34, 211, 238, 0.95)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(playheadX + 0.5, bodyTop);
      ctx.lineTo(playheadX + 0.5, minimapHeight);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(34, 211, 238, 0.18)";
      ctx.fillRect(0, minimapHeight - 4, canvasWidth, 4);
    }
  }, [
    annotations,
    canvasWidth,
    compact,
    hoveredAnnotation,
    rangeDuration,
    recordingStartMs,
    resolvedPlayheadTime,
    reviewedRanges,
    showTimeTicks,
    timeToX,
    videoSyncActive,
    viewportEnd,
    viewportStart,
    visibleRange.rangeEnd,
    visibleRange.rangeStart,
    minimapHeight,
    workspaceTheme,
  ]);

  const findAnnotationAt = useCallback(
    (x: number): TimelineAnnotation | null => {
      const time = xToTime(x);
      const tol = Math.max(0.25, xToTime(5) - xToTime(0));
      return (
        annotations.find((a) => Math.abs(a.startTime - time) <= tol) ?? null
      );
    },
    [annotations, xToTime],
  );

  const jumpPageToCanvasX = useCallback(
    (x: number, snapToAnnotation = true) => {
      const vpX1 = timeToX(viewportStart);
      const vpX2 = timeToX(viewportEnd);
      const time = resolveTimelineOverviewPointerSeek({
        pointerX: x,
        xToTime,
        pageStartX: vpX1,
        pageEndX: vpX2,
        pageDuration,
        totalDuration: safeDuration,
        findAnnotationAt: snapToAnnotation ? findAnnotationAt : undefined,
        snapToAnnotation,
      });
      onSeek(time);
    },
    [
      findAnnotationAt,
      onSeek,
      pageDuration,
      safeDuration,
      timeToX,
      viewportEnd,
      viewportStart,
      xToTime,
    ],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;

      const pageDrag = pageDragRef.current;
      if (pageDrag) {
        const nextStart = resolvePageMarkerPanSeek({
          pointerX: x,
          dragStartPointerX: pageDrag.startPointerX,
          dragStartPageStart: pageDrag.initialPageStart,
          pixelsPerSecond,
          pageDuration,
          totalDuration: safeDuration,
        });
        onSeek(nextStart);
        return;
      }

      if (isScrubbingRef.current) {
        return;
      }

      const ann = findAnnotationAt(x);
      setHoveredAnnotation(ann);
      setTooltipX(x);
    },
    [
      findAnnotationAt,
      onSeek,
      pageDuration,
      pixelsPerSecond,
      safeDuration,
    ],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      e.currentTarget.setPointerCapture?.(e.pointerId);

      const vpX1 = timeToX(viewportStart);
      const vpX2 = timeToX(viewportEnd);
      const target = hitTestTimelinePageMarker({
        pointerX: x,
        pageStartX: vpX1,
        pageEndX: vpX2,
        hitPaddingPx: 3,
      });

      if (target === "page-marker") {
        pageDragRef.current = {
          startPointerX: x,
          initialPageStart: viewportStart,
        };
        isScrubbingRef.current = true;
        return;
      }

      isScrubbingRef.current = true;
      jumpPageToCanvasX(x, true);
    },
    [jumpPageToCanvasX, timeToX, viewportEnd, viewportStart],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      pageDragRef.current = null;
      isScrubbingRef.current = false;
    },
    [],
  );

  const handlePointerLeave = useCallback(() => {
    setHoveredAnnotation(null);
    pageDragRef.current = null;
    isScrubbingRef.current = false;
  }, []);

  const handleZoomModeSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const nextMode = event.target.value as MacroOverviewZoomMode;
      onZoomModeChange(nextMode);
      if (nextMode !== "custom") {
        return;
      }
      if (!customRange) {
        onCustomRangeChange(visibleRange);
      }
    },
    [customRange, onCustomRangeChange, onZoomModeChange, visibleRange],
  );

  const handleZoomBarPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>, mode: ZoomBarDragMode) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      zoomDragRef.current = {
        mode,
        startClientX: event.clientX,
        initialRange: visibleRange,
      };
      if (zoomMode !== "custom") {
        onZoomModeChange("custom");
      }
    },
    [onZoomModeChange, visibleRange, zoomMode],
  );

  const handleZoomBarPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = zoomDragRef.current;
      const bar = zoomBarRef.current;
      if (!drag || !bar) return;

      const rect = bar.getBoundingClientRect();
      const deltaFraction =
        (event.clientX - drag.startClientX) / Math.max(1, rect.width);
      const deltaSeconds = deltaFraction * safeDuration;
      const initialSpan =
        drag.initialRange.rangeEnd - drag.initialRange.rangeStart;

      let nextRange: NavigationTimelineVisibleRange;
      if (drag.mode === "pan") {
        nextRange = {
          rangeStart: drag.initialRange.rangeStart + deltaSeconds,
          rangeEnd: drag.initialRange.rangeEnd + deltaSeconds,
        };
      } else if (drag.mode === "resize-start") {
        nextRange = {
          rangeStart: drag.initialRange.rangeStart + deltaSeconds,
          rangeEnd: drag.initialRange.rangeEnd,
        };
      } else {
        nextRange = {
          rangeStart: drag.initialRange.rangeStart,
          rangeEnd: drag.initialRange.rangeEnd + deltaSeconds,
        };
      }

      const clamped = clampNavigationTimelineVisibleRange(
        nextRange,
        safeDuration,
        Math.min(MACRO_OVERVIEW_ZOOM_MIN_SPAN_SECONDS, initialSpan),
      );
      onCustomRangeChange(clamped);
    },
    [onCustomRangeChange, safeDuration],
  );

  const handleZoomBarPointerEnd = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      zoomDragRef.current = null;
    },
    [],
  );

  const formatTick = (seconds: number) =>
    formatNavigationTimelineTickLabel(seconds, recordingStartMs);

  const thumbStartPct = (visibleRange.rangeStart / safeDuration) * 100;
  const thumbWidthPct =
    ((visibleRange.rangeEnd - visibleRange.rangeStart) / safeDuration) * 100;
  const plotLeftCssPx = Math.max(0, Math.round(plotLeftPx || 0));
  const plotInsetStyle = {
    ["--eeg-plot-left" as string]: `${plotLeftCssPx}px`,
  };

  return (
    <div
      ref={containerRef}
      className={`waveform-minimap${plotLeftCssPx > 0 ? " waveform-minimap--plot-inset" : ""}`}
      style={plotInsetStyle}
    >
      {showRangeChrome ? (
        <div className="waveform-minimap__header waveform-minimap__header--range">
          <span className="waveform-minimap__title">
            <Trans ns="features" i18nKey="ui.copy.a1b2c3d4e5f6" />
            {videoSyncActive ? (
              <span className="waveform-minimap__video-badge">
                <Trans ns="features" i18nKey="ui.copy.b3c4d5e6f7a8" />
              </span>
            ) : null}
          </span>
          <div
            ref={zoomBarRef}
            className="waveform-minimap__zoom-bar"
            style={{ height: ZOOM_BAR_HEIGHT }}
            onPointerMove={handleZoomBarPointerMove}
            onPointerUp={handleZoomBarPointerEnd}
            onPointerCancel={handleZoomBarPointerEnd}
            role="slider"
            aria-label={t("ui.copy.a1c2d3e4f5a6")}
            aria-valuemin={0}
            aria-valuemax={safeDuration}
            aria-valuenow={resolvedPlayheadTime}
          >
            <div className="waveform-minimap__zoom-bar-track" />
            <div
              className="waveform-minimap__zoom-bar-thumb"
              style={{
                left: `${thumbStartPct}%`,
                width: `${Math.max(thumbWidthPct, 100 / safeDuration)}%`,
              }}
              onPointerDown={(event) => handleZoomBarPointerDown(event, "pan")}
            >
              <span
                className="waveform-minimap__zoom-bar-handle waveform-minimap__zoom-bar-handle--start"
                onPointerDown={(event) =>
                  handleZoomBarPointerDown(event, "resize-start")
                }
              />
              <span
                className="waveform-minimap__zoom-bar-handle waveform-minimap__zoom-bar-handle--end"
                onPointerDown={(event) =>
                  handleZoomBarPointerDown(event, "resize-end")
                }
              />
            </div>
          </div>
          <label className="waveform-minimap__zoom-select">
            <span className="visually-hidden">
              <Trans ns="features" i18nKey="ui.copy.a1c2d3e4f5a6" />
            </span>
            <select
              value={zoomMode}
              onChange={handleZoomModeSelect}
              aria-label={t("ui.copy.a1c2d3e4f5a6")}
            >
              <option value="full">{t("ui.copy.f7a8b9c0d1e2")}</option>
              <option value="1h">{t("ui.copy.f8a9b0c1d2e3")}</option>
              <option value="30m">{t("ui.copy.f9a0b1c2d3e4")}</option>
              {zoomMode === "custom" ? (
                <option value="custom">{t("ui.copy.a0b1c2d3e4f5")}</option>
              ) : null}
            </select>
          </label>
        </div>
      ) : null}
      <div className="waveform-minimap__plot">
        <canvas
          ref={canvasRef}
          className={`waveform-minimap__canvas waveform-minimap__canvas--page-marker${compact ? " waveform-minimap__canvas--compact" : ""}`}
          style={compact ? { height: `${COMPACT_MINIMAP_HEIGHT}px` } : undefined}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          aria-label={t("ui.copy.a1b2c3d4e5f6", { defaultValue: "Study View" })}
        />
      </div>
      {hoveredAnnotation && (
        <div
          className="waveform-minimap__tooltip"
          style={{
            left: Math.min(
              tooltipX + 10 + plotLeftCssPx,
              canvasWidth + plotLeftCssPx - 170,
            ),
          }}
        >
          <div className="waveform-minimap__tooltip-label">
            {hoveredAnnotation.label}
          </div>
          <div className="waveform-minimap__tooltip-meta">
            {hoveredAnnotation.type} · {formatTick(hoveredAnnotation.startTime)}
          </div>
        </div>
      )}
    </div>
  );
};

export default WaveformMinimap;
